import { inject, Injectable } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { take } from 'rxjs';
import { _, TranslateService } from '@ngx-translate/core';
import { MenuItem, MessageService } from 'primeng/api';
import {
  LdActionPlace,
  LdActionType,
  LdCoordinatorDispatch,
} from '@app/main/leak-detection/services/leak-detection-coordinator/leak-detection-coordinator.actions';
import { IEvent } from '@app/api/interfaces/event.interface';
import { ContextMenuCopyService } from '@app/main/main-content/context-menu/services/context-menu-copy.service';
import { PermissionName } from '@app/core/permissions/enums/permission-name.enum';
import { LdEventStatus } from '@app/main/leak-detection/enums/ld-event-status.enum';
import { DialogEventPersistenceService } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-persistence.service';
import { DialogHideEventComponent } from '@app/main/leak-detection/context-menus/dialogs/dialog-hide-event/dialog-hide-event.component';
import { DialogEventHideConfService } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-hide-conf.service';
import { DialogTrackerService } from '@app/core/services/dialog-tracker/dialog-tracker.service';
import { LeakDetectionEventPositionService } from '@app/main/leak-detection/services/leak-detection-event-position/leak-detection-event-position.service';
import { LeakDetectionEventDeleteService } from '@app/main/leak-detection/services/leak-detection-event-delete/leak-detection-event-delete.service';
import { MessageConfigsService } from '@app/core/services/message-configs/message-configs.service';
import { ToastSeverity } from '@app/shared/components/toast-content/toast-content.component';

@Injectable({ providedIn: 'root' })
export class LdContextMenuEventService {
  private readonly translateService = inject(TranslateService);
  private readonly ngxPermissionsService = inject(NgxPermissionsService);
  private readonly contextMenuCopy = inject(ContextMenuCopyService);
  private readonly eventPersistence = inject(DialogEventPersistenceService);
  private readonly eventDeleteService = inject(LeakDetectionEventDeleteService);
  private readonly messageService = inject(MessageService);
  private readonly messageConfigsService = inject(MessageConfigsService);
  private readonly dialogTrackerService = inject(DialogTrackerService);
  private readonly eventPosition = inject(LeakDetectionEventPositionService);
  private readonly hideConf = inject(DialogEventHideConfService);

  public getMapItems(event: IEvent, dispatch: LdCoordinatorDispatch): MenuItem[] {
    if (!this.canAccessEvent()) return [];

    return this.getCommonItems(event, LdActionPlace.Map, dispatch);
  }

  public getTableItems(event: IEvent, dispatch: LdCoordinatorDispatch): MenuItem[] {
    if (!this.canAccessEvent()) return [];

    return [
      // whichever cell was clicked, it is the event's serial number that is copied
      ...this.contextMenuCopy.getCopyItems(event.serialNo),
      ...this.getCommonItems(event, LdActionPlace.TabsPanel, dispatch),
    ];
  }

  private canAccessEvent(): boolean {
    return !!this.ngxPermissionsService.getPermission(PermissionName.LeakDetection_Read_EventContextMenu);
  }

  private getCommonItems(event: IEvent, place: LdActionPlace, dispatch: LdCoordinatorDispatch): MenuItem[] {
    // an event carries no coordinates until it is located, so the entries placed by them stay disabled
    const coordinates = event.coordinates;

    // only a NEW event can be deleted — once it has moved on, it is part of the project's history
    const canDelete =
      event.status === LdEventStatus.New &&
      !!this.ngxPermissionsService.getPermission(PermissionName.LeakDetection_Write_EventDelete);

    return [
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-event-details__label`)),
        icon: 'icons/icon-event-details.svg',
        command: () => dispatch({ type: LdActionType.EventDbClick, place, item: event }),
      },
      { separator: true },
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-change-event-position__label`)),
        icon: 'icons/icon-event-change-position.svg',
        command: () => this.eventPosition.requestStart(event),
      },
      event.hidden
        ? {
            label: this.translateService.instant(_(`@@leak-detection-context-menu-unhide-event__label`)),
            icon: 'icons/icon-eye.svg',
            command: () => this.setHidden(event, false),
          }
        : {
            label: this.translateService.instant(_(`@@leak-detection-context-menu-hide-event__label`)),
            icon: 'icons/icon-eye-hide.svg',
            command: () => this.confirmHide(event),
          },
      ...(canDelete
        ? [
            {
              label: this.translateService.instant(_(`@@leak-detection-context-menu-delete-event__label`)),
              icon: 'icons/icon-delete.svg',
              command: () => this.eventDeleteService.confirmAndDelete(event),
            },
          ]
        : []),
      { separator: true },
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-show-in-street-view__label`)),
        icon: 'icons/icon-show-on-street.svg',
        iconStyle: { 'original-icon': true },
        disabled: !coordinates,
        command: () => coordinates && dispatch({ type: LdActionType.ShowInStreetView, place, position: coordinates }),
      },
    ];
  }

  private confirmHide(event: IEvent): void {
    if (this.hideConf.isBlocked()) {
      this.setHidden(event, true);

      return;
    }

    const ref = this.dialogTrackerService.regAndOpen(DialogHideEventComponent, {
      ...this.dialogTrackerService.getMainContentDialogConfig(),
      // the prompt brings its own backdrop and card, so the dialog is only a transparent frame it fills
      position: 'center',
      width: '100%',
      height: '100%',
      styleClass: '!m-0 !max-h-full !bg-transparent !shadow-none !border-0 !rounded-none',
      maskStyleClass: '!absolute inset-0 !bg-transparent !z-content-overlay-0',
    });

    ref.onClose.pipe(take(1)).subscribe((confirmed: boolean | undefined) => {
      if (confirmed) this.setHidden(event, true);
    });
  }

  private setHidden(event: IEvent, hidden: boolean): void {
    this.eventPersistence.setHidden(event.id, hidden).subscribe({
      next: () =>
        this.messageService.add({
          ...this.messageConfigsService.getMainContentMessageConfig(),
          severity: ToastSeverity.Success,
          summary: this.translateService.instant(
            hidden ? _(`@@leak-detection-event-toast__hidden`) : _(`@@leak-detection-event-toast__unhidden`),
            { I18N_VAR_EVENT_NO: event.serialNo }
          ),
        }),
      // a failed write leaves the event as it was — nothing to announce
      error: () => undefined,
    });
  }
}
